import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import { AppDispatch, RootState } from '../../../stateStore'
import {
  UserAccessToFeatures,
  UserAccessToFeaturesSliceState,
} from '../../../types/Settings/UserRolesConfiguration/userAccessToFeaturesTypes'
import { ApiLoadingState } from '../../../middleware/api/apiList'
import { LoadingState, ValidationError } from '../../../types/commonTypes'
import userAccessToFeaturesApi from '../../../middleware/api/Settings/UserRolesConfiguration/userAccessToFeaturesApi'

const getUserAccessToFeatures = createAsyncThunk<
  UserAccessToFeatures[],
  string,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>(
  'userAccessToFeatures/getUserAccessToFeatures',
  async (userId: string, thunkApi) => {
    try {
      return await userAccessToFeaturesApi.getUserAccessToFeatures(userId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getUserAccessToFeaturesForVendor = createAsyncThunk<
  UserAccessToFeatures[],
  string,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>(
  'userAccessToFeatures/getUserAccessToFeaturesForVendor',
  async (userId: string, thunkApi) => {
    try {
      return await userAccessToFeaturesApi.getUserAccessToFeaturesForVendor(
        userId,
      )
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const initialUserAccessToFeaturesState: UserAccessToFeaturesSliceState = {
  userAccessToFeatures: [],
  isLoading: ApiLoadingState.idle,
  hasLoadedPermissions: false,
  userAccessToFeaturesForVendor: [],
}

const userAccessToFeaturesSlice = createSlice({
  name: 'userAccessToFeatures',
  initialState: initialUserAccessToFeaturesState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getUserAccessToFeatures.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.userAccessToFeatures = action.payload
        state.hasLoadedPermissions = true
      })
      .addCase(getUserAccessToFeatures.pending, (state) => {
        state.isLoading = ApiLoadingState.loading
      })
      .addCase(getUserAccessToFeaturesForVendor.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.hasLoadedPermissions = true
        state.userAccessToFeatures = action.payload
      })
      .addCase(getUserAccessToFeaturesForVendor.pending, (state) => {
        state.isLoading = ApiLoadingState.loading
      })
  },
})

const userAccessToFeatures = (state: RootState): UserAccessToFeatures[] =>
  state.userAccessToFeatures.userAccessToFeatures

const userAccessToFeaturesForVendor = (
  state: RootState,
): UserAccessToFeatures[] =>
  state.userAccessToFeatures.userAccessToFeaturesForVendor

const isLoading = (state: RootState): LoadingState =>
  state.userAccessToFeatures.isLoading

const roleHasLoadedPermissions = (state: RootState): boolean =>
  state.userAccessToFeatures.hasLoadedPermissions

export const userAccessToFeaturesThunk = {
  getUserAccessToFeatures,
  getUserAccessToFeaturesForVendor,
}

const userAccessToFeaturesSelectors = {
  userAccessToFeatures,
  isLoading,
  roleHasLoadedPermissions,
  userAccessToFeaturesForVendor,
}

export const userAccessToFeaturesService = {
  ...userAccessToFeaturesThunk,
  actions: userAccessToFeaturesSlice.actions,
  selectors: userAccessToFeaturesSelectors,
}

export default userAccessToFeaturesSlice.reducer
